import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  AlertTriangle, 
  CheckCircle, 
  Globe, 
  Play,
  RefreshCw
} from 'lucide-react';
import { PortugueseTranslationFixer } from '@/utils/validation/PortugueseTranslationFixer';
import { useLanguage } from '@/context/LanguageContext';

const PortugueseTranslationTestPage = () => {
  const { t, language } = useLanguage();
  const [isFixing, setIsFixing] = React.useState(false);
  const [isFixed, setIsFixed] = React.useState(false);

  useEffect(() => {
    // Run diagnostic on page load
    PortugueseTranslationFixer.diagnoseTranslationIssues();
  }, []);

  const handleFixTranslations = async () => {
    setIsFixing(true);
    try {
      await PortugueseTranslationFixer.applyPortugueseTranslations();
      setIsFixed(true);
    } catch (error) {
      console.error('Failed to fix translations:', error);
    } finally {
      setIsFixing(false);
    }
  };

  const testStrings = [
    'Why Choose Platform',
    'Trusted By Millions', 
    'Global Coverage',
    'How It Works',
    'Find Your Therapist',
    'Anxiety Therapy',
    'Start Your Journey Today',
    'Available Online Now',
    'Verified Professionals'
  ];

  return (
    <>
      <Helmet>
        <title>Portuguese Translation Test & Fix</title>
        <meta name="description" content="Test and fix Portuguese translations" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              🇵🇹 Portuguese Translation Test & Fix
            </h1>
            <p className="text-muted-foreground">
              Diagnose and fix Portuguese translation issues on /pt pages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Current Language</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {language.toUpperCase()}
                </div>
                <Badge variant={language === 'pt' ? 'default' : 'secondary'} className="mt-2">
                  {language === 'pt' ? 'Portuguese Detected' : 'Not Portuguese'}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Translation Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  NEEDS FIX
                </div>
                <div className="text-sm text-muted-foreground">Multiple hardcoded strings</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Test Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {isFixed ? (
                    <span className="text-green-600">FIXED</span>
                  ) : (
                    <span className="text-red-600">FAILING</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Issues Detected
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Portuguese users are seeing English text on /pt pages
                  </AlertDescription>
                </Alert>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span>Multiple translation systems not synchronized</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span>Hardcoded English strings in components</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span>Translation keys not properly mapped</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span>Language detection issues on /pt route</span>
                  </div>
                </div>

                <Button 
                  onClick={handleFixTranslations}
                  disabled={isFixing}
                  className="w-full mt-4 flex items-center gap-2"
                >
                  {isFixing ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {isFixing ? 'Fixing Translations...' : 'Fix All Portuguese Translations'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Translation Test Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {testStrings.map((testString, index) => {
                    const translated = t(testString);
                    const isTranslated = translated !== testString && !translated.includes(testString);
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-3 border rounded">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{testString}</div>
                          <div className="text-xs text-muted-foreground">
                            → {translated}
                          </div>
                        </div>
                        <div className="ml-2">
                          {isTranslated ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {isFixed && (
                  <Alert className="mt-4">
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      ✅ Portuguese translations have been fixed! Please refresh the /pt page to see changes.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Current Page Translation Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded">
                  <div className="font-semibold mb-2">Platform Features</div>
                  <div className="text-sm space-y-1">
                    <div>{t('Why Choose Platform')}</div>
                    <div>{t('Trusted By Millions')}</div>
                    <div>{t('Global Coverage')}</div>
                  </div>
                </div>
                
                <div className="p-4 bg-muted rounded">
                  <div className="font-semibold mb-2">Process Steps</div>
                  <div className="text-sm space-y-1">
                    <div>{t('How It Works')}</div>
                    <div>{t('Find Your Therapist')}</div>
                    <div>{t('Book Your Session')}</div>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded">
                  <div className="font-semibold mb-2">Services</div>
                  <div className="text-sm space-y-1">
                    <div>{t('Anxiety Therapy')}</div>
                    <div>{t('Online Therapy')}</div>
                    <div>{t('Couples Counseling')}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default PortugueseTranslationTestPage;